import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import { Page } from 'components/Page';
import { ROUTE } from 'route/RouteEnums';
import {
  Grid,
  Typography,
  IconButton,
  CardMedia,
  CardContent,
  Card,
  CardProps,
  Box,
  Link,
  Button,
} from '@mui/material';
import { Container, Stack } from '@mui/system';

import { getProfileImageAPI } from 'services/profileServices';
import PrimaryContact from '../PatientInfo/PrimaryContact';
import { useAppDispatch } from 'store/hooks';
import { getInsuranceDetails, getPatientDetailsFromAdmin } from 'store/slices/userSlice';
import { getAppointmentHistory } from 'store/slices/appointmentHistorySlice';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getDefaultLandingPage } from 'utils/navigationUtils';
import { Restricted } from 'components/Restricted';
import { permissions } from 'utils/dropDownList';
import CancelAppointmentDialog from 'pages/ManageAppointment/CancelAppointmentDialog';
import { cancelAppointmentByStaffAPI } from 'services/selfSchedulingServices';
import { SCHEDULING_UNAVAILABLE_MESSAGE } from 'shared/content/constants';

/**
 * MM-1191: start of every appointment status (lower case) after which staff can no longer cancel it - cancelled,
 * late cancel, complete, no-show and bumped. ehr-sync refuses these too.
 */
const FINAL_STATUS_PREFIXES = ['cancel', 'late cancel', 'complete', 'no-show', 'no show', 'bumped'];

/**
 * MM-1191: whether staff may cancel this upcoming appointment from the chart - it must have its DrChrono
 * appointment id and not be in a final status (see FINAL_STATUS_PREFIXES).
 * appointment - an Upcoming Appointment entry of the history
 */
const isCancellable = (appointment: any): boolean => {
  if (!appointment?.chronoAppointmentId) {
    return false;
  }
  const status = String(appointment?.status ?? '')
    .trim()
    .toLowerCase();
  return !FINAL_STATUS_PREFIXES.some((prefix) => status.startsWith(prefix));
};

/**
 * MM-1191: the upcoming appointments with the one just cancelled shown as Cancelled (the others unchanged).
 * appointments - the Upcoming Appointment entries shown
 * chronoAppointmentId - DrChrono id of the appointment just cancelled
 */
const markAppointmentCancelled = (appointments: any[], chronoAppointmentId: any): any[] =>
  appointments.map((item: any) =>
    item?.chronoAppointmentId === chronoAppointmentId ? { ...item, status: 'Cancelled' } : item
  );

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * MM-1207: Appointment History always shows appointment dates and times in office time (Central
 * Time), whatever the viewer's browser time zone.
 */
const APPOINTMENT_TIME_ZONE = 'America/Chicago';

const AppointmentDetails = (props: any) => {
  const patientId = useParams().id;
  const [profilePicture, setProfilePicture] = useState<any>(null);
  const [userDetails] = useState({
    patientId: null,
    patientName: '',
    contactNumber: '',
    mcat: null,
    mfsh: null,
    wellbeing: null,
    intakePaperwork: null,
    type: null,
    upcomingAppointment: null,
  });
  const [patientDetails, setPatientDetails] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
  });
  const [upcommingAppointment, setUpcommingAppointment] = useState<any>({});
  const [pastAppointments, setPastAppointments] = useState<any>([]);
  const [insurance, setInsurance] = useState('');
  /** MM-1191: the upcoming appointment whose Cancel Appointment dialog is open, null while it is closed. */
  const [appointmentToCancel, setAppointmentToCancel] = useState<any>(null);
  /** MM-1191: true while the cancellation is being processed. */
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const StyleCard = styled(Card)<CardProps>(({ theme }) => ({
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
    border: '1px solid #EFF4FF',
    borderRadius: 12,
    background: '#FFF',
    margin: '12px 16px',
    position: 'relative',
    zIndex: 99,
    padding: 20,
    '& .MuiTypography-root': {
      position: 'relative',
    },
    '& .MuiCardContent-root:last-child': {
      padding: 0,
    },
    '.MuiTypography-h2': {
      fontSize: 20,
    },
    '& .Mui-selected': {
      background: '#9F84FF',
      color: '#FFF!important',
      borderRadius: '23px',
      width: '100%',
    },
    '& .MuiTab-root': {
      margin: '0 60px!important',
    },
  }));

  useEffect(() => {
    getPatientDetailsFromAdminPortal();
    getInsuranceData();
    getAppointmentHistoryData();
    getProfileImageAPI(patientId).then((response: any) => {
      if (response.status === 200 && response.data) {
        setProfilePicture(response.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAppointmentHistoryData = () => {
    dispatch(getAppointmentHistory(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200 && response?.data !== '') {
          const appointments: any = [];
          if (response?.data?.upcommingAppointment.length === 1) {
            // show first 2 appointments only
            const a = response?.data?.upcommingAppointment[0];
            appointments.push(a);
          } else if (response?.data?.upcommingAppointment.length > 1) {
            const a = response?.data?.upcommingAppointment[0];
            const b = response?.data?.upcommingAppointment[1];
            appointments.push(a);
            appointments.push(b);
          }
          setUpcommingAppointment(appointments);
          setPastAppointments(response?.data?.pastAppointment);
        } else {
          const message = 'Patient has not requested appointment yet.';
          toast(message, {
            type: 'info',
            icon: false,
          });
        }
      });
  };

  /**
   * MM-1191: Cancel Appointment confirmed - cancels the appointment through ehr-sync (DrChrono status Cancelled, the
   * free slot it took put back if it took one, ehr-service status Cancelled), then shows it as Cancelled at once and
   * reloads the history, so staff don't have to refresh the page.
   */
  const handleConfirmCancel = () => {
    const appointment = appointmentToCancel;
    if (!appointment) return;
    setCancelLoading(true);
    cancelAppointmentByStaffAPI(appointment.chronoAppointmentId, {
      patientId: Number(patientId),
      doctor: appointment.providerId,
      office: appointment.officeId,
      profileId: appointment.profileId,
      examRoom: appointment.examRoom,
      duration: appointment.duration,
      scheduledTime: appointment.scheduledTime,
    })
      .then((response: any) => {
        if (response?.data?.apiResult) {
          setAppointmentToCancel(null);
          toast.success('Appointment cancelled.');
          setUpcommingAppointment((current: any[]) =>
            markAppointmentCancelled(current, appointment.chronoAppointmentId)
          );
          getAppointmentHistoryData();
        } else if (response?.data?.throttled) {
          toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
        } else {
          toast.error(response?.data?.messsage ?? 'Failed to cancel appointment. Please try again.');
        }
      })
      .catch(() => {
        toast.error('Failed to cancel appointment. Please try again.');
      })
      .finally(() => setCancelLoading(false));
  };

  const getPatientDetailsFromAdminPortal = () => {
    dispatch(getPatientDetailsFromAdmin(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setPatientDetails({
            firstName: response?.data?.firstName,
            lastName: response?.data?.lastName,
            dateOfBirth: response?.data?.dateOfBirth,
          });
        }
      });
  };

  const getInsuranceData = () => {
    dispatch(getInsuranceDetails(patientId))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          setInsurance(response?.data?.payOutOfPocket ? 'No' : 'Yes');
        } else {
          setInsurance('Not available');
        }
      });
  };

  const getPatientFormattedName = () => {
    if (userDetails?.patientName) return userDetails?.patientName;
    else if (patientDetails?.firstName && patientDetails?.lastName)
      return `${patientDetails.firstName} ${patientDetails.lastName}`;
    else if (patientDetails?.firstName) return patientDetails?.firstName;
    else if (patientDetails?.lastName) return patientDetails?.lastName;
    else return '';
  };

  const getAppointmentStatusColor = (appointmentStatus: any) => {
    if (appointmentStatus === 'Confirmed') {
      return '#1570EF';
    } else if (appointmentStatus === 'Complete') {
      return '#027A48';
    } else if (appointmentStatus === 'Cancelled') {
      return '#912018';
    } else if (appointmentStatus === 'Late Cancel') {
      return '#912018';
    } else if (appointmentStatus === 'Bumped') {
      return '#912018';
    } else if (appointmentStatus === 'No-show') {
      return '#912018';
    } else if (appointmentStatus === 'Scheduled') {
      return '#1570EF';
    }
    return '#1570EF';
  };

  const loadPastAppointments = () => {
    return (
      <Grid item xs={6} md={12} alignItems={'flex-start'}>
        <Typography variant='h2' sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.4, marginTop: '16px' }}>
          Past Appointments
        </Typography>
        {pastAppointments.map((item: any, index: number) => {
          return (
            <Grid key={index} item alignItems={'flex-start'} borderBottom={'1px solid #EAECF0'}>
              <Typography
                sx={{
                  color: '#475467',
                  fontFamily: 'Graphik Trial',
                  fontSize: '18px',
                  lineHeight: '24px',
                  mb: '8px',
                  marginTop: '24px',
                }}
              >
                Appointment Date
              </Typography>
              <Stack spacing={1} direction={'column'}>
                <Stack direction='row' spacing={1} alignItems={'center'}>
                  <CardMedia
                    component={'img'}
                    src='../../images/calendar.svg'
                    alt='card-img1'
                    sx={{ height: '20px', width: '20px', mb: '3px' }}
                  />
                  <Stack direction='row' spacing={1} alignItems={'center'}>
                    <Typography sx={{ color: '#2C3446', fontSize: '18px', lineHeight: '28px', fontWeight: 500 }}>
                      {item?.upcomingAppointmentDateTime
                        ? dayjs
                            .utc(item?.upcomingAppointmentDateTime)
                            .tz(APPOINTMENT_TIME_ZONE)
                            .format('MMM DD, YYYY')
                        : ''}
                    </Typography>
                    <Typography sx={{ color: '#2C3446', fontSize: '18px', lineHeight: '28px', fontWeight: 500 }}>
                      at
                    </Typography>
                    <Typography sx={{ color: '#2C3446', fontSize: '18px', lineHeight: '28px', fontWeight: 500 }}>
                      {item?.upcomingAppointmentDateTime
                        ? dayjs
                            .utc(item?.upcomingAppointmentDateTime.substring(0, 23))
                            .tz(APPOINTMENT_TIME_ZONE)
                            .format('hh:mm A')
                        : ''}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Stack direction='row' spacing={1} alignItems={'center'}>
                <Box
                  bgcolor={getAppointmentStatusColor(item.status)}
                  borderRadius='12px'
                  marginTop='10px'
                  marginBottom='16px'
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 500,
                      paddingLeft: '12px',
                      paddingRight: '12px',
                      paddingTop: '2px',
                      paddingBottom: '2px',
                    }}
                  >
                    {item.status === '' && item?.upcomingAppointmentDateTime ? 'Scheduled' : item.status}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Page backDir={getDefaultLandingPage()} buttonText='Dashboard'>
      <Container component='main' maxWidth='md'>
        <Grid container spacing={2}>
          <Grid item xs={10} display={'flex'} alignItems={'center'}>
            <Typography variant='h5' gutterBottom>
              Appointment History
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <IconButton>
              <CardMedia
                component={'img'}
                src={profilePicture !== null ? `data:image/png;base64, ${profilePicture}` : '/images/Group 9513.png'}
                alt='patient picture'
                sx={{
                  border: '1px solid #333',
                  borderRadius: '50%',
                  height: { xs: '70px', md: '106px' },
                  width: { xs: '70px', md: '106px' },
                  cursor: 'default',
                }}
              />
            </IconButton>
          </Grid>
          <Grid item xs={12}>
            <StyleCard sx={{ borderTop: '12px solid #FFC44D !important' }}>
              <CardContent>
                <Grid container borderBottom={'1px solid #EAECF0'}>
                  <Grid item xs={6} md={8} alignItems={'flex-start'}>
                    <Typography
                      variant='h2'
                      sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.4, textTransform: 'capitalize' }}
                    >
                      {getPatientFormattedName()}
                    </Typography>
                    <Typography variant='h3' sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.4 }}>
                      {insurance !== '' ? (
                        <>
                          Insurance : <b> {insurance}</b>
                        </>
                      ) : null}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={4} display='flex' alignItems={'flex-start'} justifyContent='end'>
                    {patientDetails?.dateOfBirth && (
                      <Typography variant='h2' sx={{ span: { fontSize: 10 }, fontSize: 20, fontWeight: 500 }}>
                        <span>DOB:</span> {dayjs(patientDetails?.dateOfBirth).format('MMM DD, YYYY')}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                <PrimaryContact patientId={Number(patientId)} showHistoryLink={true} />
              </CardContent>
            </StyleCard>
          </Grid>

          <Grid item xs={12}>
            <StyleCard>
              <CardContent>
                <Grid container>
                  <Grid item xs={6} md={12} alignItems={'flex-start'} borderBottom={'1px solid #EAECF0'} mb='4px'>
                    <Typography variant='h2' sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.4 }}>
                      Upcoming Appointment
                    </Typography>
                    {upcommingAppointment.length > 0 ? (
                      upcommingAppointment.map((u: any, i: number) => {
                        return (
                          <Box
                            key={u?.upcomingAppointmentDateTime}
                            sx={{
                              padding: '0px 20px',
                              borderLeft: '3px solid #FFC44D',
                              margin: '16px 0 26px 0',
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#475467',
                                fontFamily: 'Graphik Trial',
                                fontSize: '18px',
                                lineHeight: '24px',
                                mb: '8px',
                              }}
                            >
                              Appointment Date
                            </Typography>

                            <Stack spacing={1} direction={'column'}>
                              <Stack direction='row' spacing={1} alignItems={'center'}>
                                <CardMedia
                                  component={'img'}
                                  src='../../images/calendar-check.svg'
                                  alt='card-img1'
                                  sx={{ height: '20px', width: '20px', mb: '4px' }}
                                />
                                <Stack direction='row' spacing={1} alignItems={'center'}>
                                  <Typography sx={{ color: '#2C3446', fontSize: '18px', fontWeight: 500 }}>
                                    {dayjs
                                      .utc(u?.upcomingAppointmentDateTime)
                                      .tz(APPOINTMENT_TIME_ZONE)
                                      .format('MMM DD, YYYY')}
                                  </Typography>
                                  <Typography sx={{ color: '#2C3446', fontSize: '18px', fontWeight: 500 }}>
                                    at
                                  </Typography>
                                  <Typography sx={{ color: '#2C3446', fontSize: '18px', fontWeight: 500 }}>
                                    {dayjs
                                      .utc(u?.upcomingAppointmentDateTime.substring(0, 23))
                                      .tz(APPOINTMENT_TIME_ZONE)
                                      .format('hh:mm A')}
                                  </Typography>
                                </Stack>
                                {/* MM-1191: Cancel button (opens the Cancel Appointment dialog) on the right end of the
                                    appointment date row - staff only, while it can still be
                                    cancelled */}
                                {isCancellable(u) && (
                                  <Restricted permission={permissions.VIEW_EHR_DETAILS}>
                                    <Button
                                      variant='outlined'
                                      color='error'
                                      size='small'
                                      sx={{ marginLeft: 'auto !important', textTransform: 'none' }}
                                      onClick={() => setAppointmentToCancel(u)}
                                    >
                                      Cancel
                                    </Button>
                                  </Restricted>
                                )}
                              </Stack>
                            </Stack>

                            <Typography
                              sx={{
                                color: '#475467',
                                fontFamily: 'Graphik Trial',
                                fontSize: '18px',
                                lineHeight: '24px',
                                mt: '12px',
                                mb: '4px',
                              }}
                            >
                              Provider Name
                            </Typography>
                            <Stack spacing={1} direction={'row'}>
                              <CardMedia
                                component={'img'}
                                src='../../images/stethoscope.svg'
                                alt='card-img1'
                                sx={{ height: '20px', width: '20px', mb: '3px' }}
                              />
                              <Typography sx={{ color: '#2C3446', fontSize: '18px', fontWeight: 500 }}>
                                {u?.providerName || ''}
                              </Typography>
                            </Stack>
                            <Typography
                              sx={{
                                color: '#475467',
                                fontFamily: 'Graphik Trial',
                                fontSize: '18px',
                                lineHeight: '24px',
                                mt: '12px',
                                mb: '4px',
                              }}
                            >
                              Type
                            </Typography>
                            <Stack spacing={1} direction={'row'}>
                              <CardMedia
                                component={'img'}
                                src='../../images/type.svg'
                                alt='card-img1'
                                sx={{ height: '20px', width: '20px', mb: '3px', objectFit: 'contain' }}
                              />
                              <Typography
                                sx={{
                                  color: '#344054',
                                  fontSize: '18px',
                                  fontWeight: 500,
                                  fontStyle: 'bold',
                                  lineHeight: '28px',
                                }}
                              >
                                {u?.evaluation ? u?.evaluation : ''}
                                {u?.consultationType !== null ? ', ' : ''}
                                {u?.consultationType ? u?.consultationType : ''}

                                {u?.consultationType === 'Virtual Meeting' ? (
                                  <>
                                    -{' '}
                                    <Link href={u?.virtualMeetingLink} fontSize={'18px !important'} target='_blank'>
                                      Click Here
                                    </Link>
                                  </>
                                ) : null}
                              </Typography>
                            </Stack>
                            <Typography
                              sx={{
                                color: '#475467',
                                fontFamily: 'Graphik Trial',
                                fontSize: '18px',
                                lineHeight: '24px',
                                mt: '12px',
                                mb: '4px',
                              }}
                            >
                              Location
                            </Typography>
                            <Stack spacing={1} direction={'row'}>
                              <CardMedia
                                component={'img'}
                                src='../../images/location.svg'
                                alt='card-img1'
                                sx={{ height: '20px', width: '20px', mb: '3px', objectFit: 'contain' }}
                              />
                              <Typography
                                sx={{
                                  color: '#344054',
                                  fontSize: '18px',
                                  fontWeight: 500,
                                  fontStyle: 'bold',
                                  lineHeight: '28px',
                                }}
                              >
                                {u?.officeAddress ? u?.officeAddress : ''}
                                {u?.virtualMeetingLink == null ? '' : '- Virtual'}
                              </Typography>
                            </Stack>

                            <Stack direction='row' spacing={1} alignItems={'center'}>
                              <Box bgcolor={getAppointmentStatusColor(u?.status)} borderRadius='12px' marginTop='10px'>
                                <Typography
                                  sx={{
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    paddingLeft: '12px',
                                    paddingRight: '12px',
                                    paddingTop: '2px',
                                    paddingBottom: '2px',
                                  }}
                                >
                                  {u?.status === '' && u?.upcomingAppointmentDateTime ? 'Scheduled' : u?.status}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          padding: '0px 10px',
                          margin: '16px 0',
                        }}
                      >
                        <Typography>No Upcoming Appointments.</Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6} md={12} alignItems={'flex-start'}>
                    {pastAppointments && pastAppointments?.length > 0 && loadPastAppointments()}
                  </Grid>
                </Grid>
              </CardContent>
            </StyleCard>
          </Grid>
        </Grid>
      </Container>
      {/* MM-1191: the patient's own Cancel Appointment confirmation, reused for staff */}
      <CancelAppointmentDialog
        open={appointmentToCancel !== null}
        loading={cancelLoading}
        onKeep={() => setAppointmentToCancel(null)}
        onConfirmCancel={handleConfirmCancel}
      />
    </Page>
  );
};

export default AppointmentDetails;
